import React, { useState, useRef, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  Send, 
  Sparkles, 
  Loader2, 
  Save, 
  RefreshCw,
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AIWorkflowBuilder = () => {
  const navigate = useNavigate();
  const [model, setModel] = useState('gpt5');
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const handleGenerateWorkflow = async () => {
    if (!prompt.trim()) return;

    const userMessage = { role: 'user', content: prompt };
    setChatHistory(prev => [...prev, userMessage]);
    setLoading(true);
    setPrompt('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/generate-workflow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt: userMessage.content,
          model: model,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate workflow');
      }

      const generatedWorkflow = await response.json();
      setWorkflow(generatedWorkflow);

      const assistantMessage = {
        role: 'assistant',
        content: `I've created a workflow for "${generatedWorkflow.workflow_name}" with ${generatedWorkflow.steps.length} steps. You can see the preview on the right and refine it further if needed.`,
        workflow: generatedWorkflow
      };
      setChatHistory(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error generating workflow:', error);
      const errorMessage = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
        error: true
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefineWorkflow = async () => {
    if (!refinementPrompt.trim() || !workflow) return;

    const userMessage = { role: 'user', content: refinementPrompt };
    setChatHistory(prev => [...prev, userMessage]);
    setLoading(true);
    setRefinementPrompt('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/ai-assistant/refine-workflow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: model,
          feedback: userMessage.content,
          workflow: workflow,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to refine workflow');
      }

      const refinedWorkflow = await response.json();
      setWorkflow(refinedWorkflow);

      const assistantMessage = {
        role: 'assistant',
        content: `I've updated the workflow based on your feedback. Check out the changes in the preview.`,
        workflow: refinedWorkflow
      };
      setChatHistory(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error refining workflow:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error while refining the workflow. Please try again.',
        error: true
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWorkflow = () => {
    if (!workflow) return;

    // Create the key in the same format as CustomStepsModal
    const key = workflow.subcategory 
      ? `${workflow.listing_type}___${workflow.category}___${workflow.subcategory}`
      : `${workflow.listing_type}___${workflow.category}___`;

    // Get existing workflows from localStorage
    const stored = localStorage.getItem('customSteps');
    const savedWorkflows = stored ? JSON.parse(stored) : {};

    // Save the workflow with the proper structure
    savedWorkflows[key] = {
      listingType: workflow.listing_type,
      category: workflow.category,
      subcategory: workflow.subcategory || '',
      steps: workflow.steps
    };

    localStorage.setItem('customSteps', JSON.stringify(savedWorkflows));

    // Show success message
    const successMessage = {
      role: 'assistant',
      content: `✅ Workflow saved successfully! You can now use it in the Settings > Workflows section.`,
    };
    setChatHistory(prev => [...prev, successMessage]);

    // Navigate to settings after a short delay
    setTimeout(() => {
      navigate('/settings', { state: { activeTab: 'workflows' } });
    }, 2000);
  };

  const renderFieldPreview = (field) => {
    const baseClasses = "w-full p-2 border rounded-lg text-sm";
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            className={baseClasses}
            placeholder={field.placeholder}
            disabled
            rows={3}
          />
        );
      case 'select':
        return (
          <select className={baseClasses} disabled>
            <option>{field.placeholder || 'Select...'}</option>
            {field.options?.map((opt, idx) => (
              <option key={opt.id || opt.name || `${opt.title || "opt"}-${idx}`}>{opt}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <div className="flex items-center gap-2">
            <input type="checkbox" disabled className="h-4 w-4" />
            <span className="text-sm">{field.label}</span>
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((opt, idx) => (
              <div key={opt.id || opt.name || `${opt.title || "opt"}-${idx}`} className="flex items-center gap-2">
                <input type="radio" disabled className="h-4 w-4" name={field.name} />
                <span className="text-sm">{opt}</span>
              </div>
            ))}
          </div>
        );
      case 'daterange':
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">Start Date</label>
                <input
                  type="date"
                  className={baseClasses}
                  disabled
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">End Date</label>
                <input
                  type="date"
                  className={baseClasses}
                  disabled
                />
              </div>
            </div>
            {field.validation?.maxDays && (
              <p className="text-xs text-gray-500">
                Maximum {field.validation.maxDays} days between dates
              </p>
            )}
          </div>
        );
      case 'multicheckbox':
        return (
          <div className="space-y-2 border rounded-lg p-3 bg-gray-50">
            <p className="text-xs font-medium text-gray-700 mb-2">Select all that apply:</p>
            {field.options?.map((opt, idx) => (
              <div key={opt.id || opt.name || `${opt.title || "opt"}-${idx}`} className="flex items-center gap-2">
                <input type="checkbox" disabled className="h-4 w-4" />
                <span className="text-sm">{opt}</span>
              </div>
            ))}
          </div>
        );
      case 'buttonselect':
        return (
          <div className="space-y-2">
            <p className="text-xs text-gray-600 mb-2">Select one or more:</p>
            <div className="flex flex-wrap gap-2">
              {field.options?.map((opt, idx) => (
                <button
                  key={opt.id || opt.name || `${opt.title || "opt"}-${idx}`}
                  disabled
                  className="px-4 py-2 border rounded-lg text-sm bg-white hover:bg-blue-50 hover:border-blue-500 transition-colors disabled:cursor-not-allowed"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        );
      case 'imageselect':
        return (
          <div className="space-y-2">
            <p className="text-xs text-gray-600 mb-2">Choose an option:</p>
            <div className="grid grid-cols-3 gap-3">
              {field.imageOptions?.map((imgUrl, idx) => (
                <div
                  key={imgUrl.id || imgUrl.name || `${imgUrl.title || "imgUrl"}-${idx}`}
                  className="border-2 rounded-lg p-2 cursor-pointer hover:border-blue-500 transition-colors"
                >
                  <div className="aspect-square bg-gray-200 rounded-md flex items-center justify-center overflow-hidden">
                    {imgUrl.startsWith('http') ? (
                      <img src={imgUrl} alt={`Option ${idx + 1}`} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs text-gray-500">Image {idx + 1}</span>
                    )}
                  </div>
                  <p className="text-xs text-center mt-1 text-gray-700">{field.options?.[idx] || `Option ${idx + 1}`}</p>
                </div>
              )) || (
                <div className="col-span-3 border-2 border-dashed rounded-lg p-8 text-center text-gray-400">
                  <p className="text-sm">Visual image selection</p>
                </div>
              )}
            </div>
          </div>
        );
      default:
        return (
          <input
            type={field.type}
            className={baseClasses}
            placeholder={field.placeholder}
            disabled
          />
        );
    }
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex gap-6">
        {/* Left Sidebar - Chat Interface */}
        <div className="w-2/5 flex flex-col bg-white rounded-lg shadow-sm border">
          {/* Header */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-6 w-6 text-purple-600" />
              <h2 className="text-xl font-bold">AI Workflow Builder</h2>
            </div>
            
            {/* Model Selector */}
            <div className="flex gap-2">
              <Button
                variant={model === 'gpt5' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModel('gpt5')}
                className="flex-1"
              >
                GPT-5
              </Button>
              <Button
                variant={model === 'claude' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setModel('claude')}
                className="flex-1"
              >
                Claude
              </Button>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatHistory.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <Sparkles className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Start Building Your Workflow</p>
                <p className="text-sm">Describe the workflow you want to create in natural language.</p>
                <div className="mt-6 space-y-2 text-left bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-700 text-sm">Example prompts:</p>
                  <ul className="text-xs space-y-1 text-gray-600">
                    <li>• "Create a property listing workflow with location, pricing, and amenities"</li>
                    <li>• "Build a service booking form with date range picker (max 30 days), time slot selection using buttons, and service type selection"</li>
                    <li>• "Design an event registration with attendee info, dietary preferences (multiple checkboxes), and day selection (button style)"</li>
                    <li>• "Create a delivery order form with date range (max 7 days), delivery time slots (button select), and package type (image selection)"</li>
                  </ul>
                </div>
              </div>
            ) : (
              chatHistory.map((message, index) => (
                <div
                  key={message.id || message.name || `${message.title || "message"}-${index}`}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : message.error
                        ? 'bg-red-50 text-red-900 border border-red-200'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                </div>
              ))
            )}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg p-3">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 border-t bg-gray-50">
            {!workflow ? (
              <div className="flex gap-2">
                <Textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Describe the workflow you want to create..."
                  className="flex-1 min-h-[80px] resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleGenerateWorkflow();
                    }
                  }}
                />
                <Button
                  onClick={handleGenerateWorkflow}
                  disabled={loading || !prompt.trim()}
                  className="self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Input
                    value={refinementPrompt}
                    onChange={(e) => setRefinementPrompt(e.target.value)}
                    placeholder="Refine the workflow... (e.g., 'Add a video upload field')"
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleRefineWorkflow();
                      }
                    }}
                  />
                  <Button
                    onClick={handleRefineWorkflow}
                    disabled={loading || !refinementPrompt.trim()}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  onClick={handleSaveWorkflow}
                  className="w-full"
                  disabled={loading}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Workflow
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Preview */}
        <div className="flex-1 bg-white rounded-lg shadow-sm border overflow-y-auto">
          {!workflow ? (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <Sparkles className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Workflow preview will appear here</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              {/* Workflow Header */}
              <div className="mb-6 pb-4 border-b">
                <h2 className="text-2xl font-bold mb-2">{workflow.workflow_name}</h2>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{workflow.listing_type}</Badge>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                  <Badge variant="outline">{workflow.category}</Badge>
                  {workflow.subcategory && (
                    <>
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                      <Badge variant="outline">{workflow.subcategory}</Badge>
                    </>
                  )}
                </div>
              </div>

              {/* Steps Preview */}
              <div className="space-y-6">
                {workflow.steps?.map((step, stepIndex) => (
                  <Card key={stepIndex} className="border-l-4 border-l-blue-500">
                    <CardHeader className="pb-3">
                      <div className="flex items-start gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold text-sm flex-shrink-0">
                          {step.step_number}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg">{step.title}</CardTitle>
                          <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                        </div>
                        <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {step.fields?.map((field, fieldIndex) => (
                          <div key={fieldIndex} className="space-y-1">
                            <Label className="text-sm font-medium">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </Label>
                            {renderFieldPreview(field)}
                            {field.helpText && (
                              <p className="text-xs text-gray-500">{field.helpText}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AIWorkflowBuilder;
